<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">
      Manage Employee Managers
    </h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      :list-options="list_options"
      @reset_table="reset_table"
      @options_changed="update_table($event)"
      @filter_changed="update_filter($event)"
      :schema="schema"
      :query-select-options="options"
      :new-to="{ name: 'employee_manager.create', params: { id: 0 } }"
      new-button-text="New Employee Manager"
    >
      <template slot="middle">
        <o-list-multi-actions
          :schema="schema"
          :selection="selected"
          @delete="doDeleteItems"
          @merge="doMergeItems"
          enable-delete
          enable-merge
        />
      </template>
    </o-list-toolbar>

    <b-table
      striped
      bordered
      :fields="fields"
      :items="items"
      head-variant="light"
      class="o-list"
      thead-class="o-gradient-header"
      @row-selected="onRowSelected"
      :selectable="selected.selectable"
      :key="selected.selectable"
      no-local-sorting
      @sort-changed="change_sort"
      :sort-by="list_options.sort"
      :sort-desc="list_options.order === 'desc'"
    >
      <template v-slot:cell(_selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>
      <template
        slot="top-row"
        slot-scope="{ columns, fields }"
        v-if="filterRow"
      >
        <td :key="field.key" class="p-0" v-for="field in fields">
          <span v-if="field.key === 'employee'">
            <o-form-select
              v-model="list_options.qbe.employee"
              :options="employeeManagers"
              text-field="full_name"
              value-field="id"
              sort-field="full_name"
              blank-option-text=""
              :loading="optionEmployeeWorking"
              @change="filter_changed"
              name="employee"
            >
              <template v-slot:first>
                <option :value="null"></option>
              </template>
            </o-form-select>
          </span>
          <span v-if="field.key === 'employee_type'">
            <o-form-select
              v-model="list_options.qbe.employee_type"
              :options="optionCodeEmployeeTypeItems"
              text-field="description"
              value-field="id"
              sort-field="description"
              blank-option-text=""
              :loading="optionCodeEmployeeTypeWorking"
              @change="filter_changed"
              name="employee_type"
            >
              <template v-slot:first>
                <option :value="null"></option>
              </template>
            </o-form-select>
          </span>
        </td>
      </template>
      <template slot="cell(employee)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'employee_manager.edit', params: { id: item.id } }"
          :when="first_column_key === 'employee'"
        >
          <lookup-display
            :list="optionEmployeeItemsById"
            :value="item.employee"
            display-field="full_name"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>
      <template slot="cell(employee_type)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'employee_manager.edit', params: { id: item.id } }"
          :when="first_column_key === 'employee_type'"
        >
          <lookup-display
            :list="optionCodeEmployeeTypeItemsById"
            :value="item.employee_type"
            display-field="description"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'employee_manager.create' }"
          >create a new item</router-link
        >
      </template>
    </b-table>

    <o-list-paging
      :limit="list_options.limit"
      :page-options="rows_per_page_items"
      :offset="list_options.offset"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style></style>

<script>
import {
  CreateCodeTableFetchMixin,
  CreateGroupPermissionMixin,
  CreateListPageMixin,
  MultiActionsMixin,
} from "../../../vue-ocom/mixins";
import {
  fieldToTableColumn,
  GetSearchFields,
} from "../../../vue-ocom/schema/schema_utils";
import { cleanUpModel } from "../../../vue-ocom/schema/schema_vuetify";
import schema from "../../schema/index";

import OListToolbar from "../../../vue-ocom/components/list/OListToolbar";
import OListPaging from "../../../vue-ocom/components/list/OListPaging";
import DefaultDisplay from "../../../vue-ocom/components/display/DefaultDisplay";
import DateDisplay from "../../../vue-ocom/components/display/DateDisplay";
import NumberDisplay from "../../../vue-ocom/components/display/NumberDisplay";
import BooleanDisplay from "../../../vue-ocom/components/display/BooleanDisplay";
import LookupDisplay from "../../../vue-ocom/components/display/LookupDisplay";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad";
import EmailIcon from "vue-material-design-icons/Email";
import LinkIcon from "vue-material-design-icons/Link";

export default {
  components: {
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
  },
  mixins: [
    CreateListPageMixin("EmployeeManager", "employee_manager"),
    MultiActionsMixin,
    CreateGroupPermissionMixin("EmployeeManager"),
    CreateCodeTableFetchMixin(["Employee", "CodeEmployeeType", "Group"]),
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(
        schema.EmployeeManager,
        {}
      ) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.EmployeeManager),

      filterRow: true /* Set to False to disable filter row */,
      filter_list: [
        { name: "All", code: "all", options: { offset: 0, sort: "id" } },
      ],

      user_column_selection:
        null /* If user can customise the columns set them here */,
      column_override_params: {
        /* Put any column overrides here key by fieldname */
      },
    };
  },
  computed: {
    options() {
      return {
        Employee: {
          options: this.optionEmployeeItems,
          textField: "full_name",
          valueField: "id",
        },
        CodeEmployeeType: {
          options: this.optionCodeEmployeeTypeItems,
          textField: "description",
          valueField: "id",
        },
      };
    },
    employeeManagers() {
      const manager_group_id = this.optionGroupItems.filter((group) => {
        return group.name === "Manager";
      })[0].id;

      const employee_managers = this.optionEmployeeItems.filter((employee) => {
        if (employee.login) {
          return employee.login.groups.includes(manager_group_id);
        }
      });

      return employee_managers;
    },
    fields() {
      const vm = this;
      const selectedColumns = this.user_column_selection || [
        "employee",
        "employee_type",
      ];
      const fields = selectedColumns.map(function (item) {
        return fieldToTableColumn(
          vm.schema,
          item,
          vm.column_override_params[item]
        );
      });

      return this.addSelectionColums(
        this.filterTableColumns(this.schema, fields)
      );
    },
    first_column_key() {
      if (this.fields) {
        return this.fields[0].key;
      } else {
        return null;
      }
    },
  },
};
</script>
